/**
 * useIPC — Tauri IPC 通信统一封装
 * 所有组件调用 Rust 命令必须通过此 composable，禁止直接 invoke()
 *
 * 双导出模式：
 * - ipcCall<T>(command, args?) — 无状态版本，任意上下文可用（如 api/client.ts）
 * - useIPC() — composable 版本，组件内使用
 */
import { ref } from 'vue'
import { platformInvoke } from '@/platform/bridge'

// ─── Mock 支持（测试用） ────────────────────────────────
type MockHandler = (args?: Record<string, unknown>) => unknown | Promise<unknown>
export interface IPCOptions {
  silent?: boolean
  timeout?: number
  signal?: AbortSignal
}
let mockHandlers: Map<string, MockHandler> | null = null

export function setIPCMocks(handlers: Record<string, MockHandler>) {
  mockHandlers = new Map(Object.entries(handlers))
}

/** 追加 IPC Mock handler（不覆盖已有的） */
export function addIPCMocks(handlers: Record<string, MockHandler>) {
  if (!mockHandlers) {
    mockHandlers = new Map()
  }
  for (const [key, handler] of Object.entries(handlers)) {
    mockHandlers.set(key, handler)
  }
}

export function clearIPCMocks() {
  mockHandlers = null
}

// ─── 无状态 IPC 调用（核心） ─────────────────────────────
function abortError(reason?: unknown): Error {
  if (reason instanceof Error) return reason
  const error = new Error(typeof reason === 'string' ? reason : 'IPC 请求已取消')
  error.name = 'AbortError'
  return error
}

async function awaitWithCancellation<T>(
  operation: () => Promise<T>,
  options?: IPCOptions,
): Promise<T> {
  const signal = options?.signal
  if (signal?.aborted) throw abortError(signal.reason)

  const timeout =
    typeof options?.timeout === 'number' && Number.isFinite(options.timeout) && options.timeout > 0
      ? options.timeout
      : undefined
  if (!signal && timeout === undefined) return operation()

  let timer: ReturnType<typeof setTimeout> | undefined
  let onAbort: (() => void) | undefined
  const cancellation = new Promise<never>((_, reject) => {
    onAbort = () => reject(abortError(signal?.reason))
    signal?.addEventListener('abort', onAbort, { once: true })
    if (timeout !== undefined) {
      timer = setTimeout(() => reject(abortError('IPC 请求超时')), timeout)
    }
  })

  try {
    return await Promise.race([operation(), cancellation])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
    if (signal && onAbort) signal.removeEventListener('abort', onAbort)
  }
}

export async function ipcCall<T = unknown>(
  command: string,
  args?: Record<string, unknown>,
  options?: IPCOptions,
): Promise<T> {
  try {
    const result = await awaitWithCancellation(
      async () => {
        // 测试环境使用 Mock
        if (mockHandlers?.has(command)) {
          const handler = mockHandlers.get(command)!
          return (await handler(args)) as T
        }
        return platformInvoke<T>(command, args)
      },
      options,
    )
    return result
  } catch (err) {
    if (!options?.silent && (err as Error)?.name !== 'AbortError') {
      console.error(`[IPC] ${command} 调用失败:`, err)
    }
    if (err instanceof Error) throw err
    // Tauri IPC 错误是序列化的 Rust enum，如 { "Network": "msg" } 或 "TokenExpired"
    const msg =
      typeof err === 'string'
        ? err
        : typeof err === 'object' && err !== null
          ? Object.values(err as Record<string, unknown>).join(': ')
          : String(err)
    const error = new Error(msg)
    // 保留原始错误类型标签，方便前端识别（如 "Network"、"Auth"）
    if (typeof err === 'object' && err !== null) {
      (error as unknown as Record<string, unknown>).ipcErrorType = Object.keys(
        err as Record<string, unknown>,
      )[0]
    }
    throw error
  }
}

// ─── Composable 版本（组件内使用） ──────────────────────
export function useIPC() {
  const loading = ref(false)
  const error = ref<Error | null>(null)
  let callGeneration = 0

  async function call<T = unknown>(
    command: string,
    args?: Record<string, unknown>,
  ): Promise<T | null> {
    const generation = ++callGeneration
    loading.value = true
    error.value = null
    try {
      const result = await ipcCall<T>(command, args)
      return result
    } catch (err) {
      if (generation === callGeneration) {
        error.value = err instanceof Error ? err : new Error(String(err))
      }
      return null
    } finally {
      if (generation === callGeneration) loading.value = false
    }
  }

  return { call, loading, error }
}
